import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Character } from '@/components/game/Character'
import type { CutsceneFrame } from '@code-arcade/engine'

// Character color map — matches CLAUDE.md cast
const CHARACTER_COLORS: Record<string, string> = {
  'dev-lama': '#f7c948',
  'chad-mercer': '#60a5fa',
  'stacy-queue': '#fb7185',
  'barry-holt': '#4ade80',
  'priya-nair': '#a855f7',
  'raj-lohani': '#f97316',
  'grant-fielding': '#94a3b8',
  'vera-lox': '#f43f5e',
  'the-algorithm': '#4ecdc4',
}

const CHARACTER_NAMES: Record<string, string> = {
  'dev-lama': 'Dev Lama',
  'chad-mercer': 'Chad Mercer',
  'stacy-queue': 'Stacy Queue',
  'barry-holt': 'Barry Holt',
  'priya-nair': 'Priya Nair',
  'raj-lohani': 'Raj Lohani',
  'grant-fielding': 'Grant Fielding',
  'vera-lox': 'Vera Lox',
  'the-algorithm': 'The Algorithm',
}

interface CutscenePanelProps {
  frames: CutsceneFrame[]
  onComplete: () => void
  allowSkip?: boolean
}

export function CutscenePanel({ frames, onComplete, allowSkip = true }: CutscenePanelProps) {
  const [frameIndex, setFrameIndex] = useState(0)
  const frame = frames[frameIndex]

  if (!frame) return null

  const isLast = frameIndex === frames.length - 1

  const advance = () => {
    if (isLast) {
      onComplete()
    } else {
      setFrameIndex((i) => i + 1)
    }
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={frameIndex}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2 }}
        className="bg-arcade-surface border border-arcade-border rounded-xl p-4 flex gap-4 items-start"
      >
        <Character
          characterId={frame.character}
          mood={frame.mood}
          size={56}
          color={CHARACTER_COLORS[frame.character] ?? '#7c3aed'}
        />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-mono text-arcade-neon mb-1">
            {CHARACTER_NAMES[frame.character] ?? frame.character}
          </p>
          <p className="text-sm text-gray-200 leading-relaxed">{frame.dialogue}</p>
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={advance}
              className="px-4 py-1.5 bg-arcade-accent hover:bg-violet-500 text-white text-xs font-bold rounded-lg transition-colors"
            >
              {isLast ? 'Let\'s go →' : 'Next →'}
            </button>
            {allowSkip && !isLast && (
              <button
                onClick={onComplete}
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                Skip cutscene
              </button>
            )}
            <span className="ml-auto text-xs text-gray-600 font-mono">
              {frameIndex + 1} / {frames.length}
            </span>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
